import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCountryById, updateCountry } from '../services/api.ts';
import { ArrowLeft, Globe, Loader } from 'lucide-react';
import './EditCountry.css';

const EditCountry: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    enable: true
  });

  useEffect(() => {
    const loadCountry = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await fetchCountryById(id);
        setFormData({
          name: data.name || '',
          code: data.code || '',
          enable: data.enable || true
        });
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load country');
        console.error('Error loading country:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCountry();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setError(null);
      await updateCountry(id, formData);
      navigate('/dashboard/countries');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to update country';
      setError(errorMessage);
      console.error('Update error:', err.response?.data);
    }
  };

  if (loading) {
    return (
      <div className="edit-country">
        <div className="loading-container">
          <Loader className="loading-spinner-icon" size={40} />
          <p>Loading country...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-country">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/countries')}>
          <ArrowLeft size={20} />
          Back to Countries
        </button>
        <h2>Edit Country</h2>
      </div>

      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-section">
            <h3 className="section-title">Country Information</h3>
            
            <div className="form-group">
              <label>Country Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter country name"
                required
              />
            </div>

            <div className="form-group">
              <label>Country Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="Enter country code (e.g., US, CA, GB)"
                required
              />
              <p className="field-hint">ISO 3166-1 alpha-2 code for the country</p>
            </div>

            <div className="form-switches">
              <div className="switch-group">
                <div className="switch-content">
                  <label className="switch-label">Active Status</label>
                  <p className="switch-description">Enable this country for selection</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.enable}
                    onChange={(e) => setFormData({ ...formData, enable: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Preview</h3>
            
            <div className="preview-card">
              <div className="preview-header">
                <div className="preview-icon">
                  <Globe size={24} />
                </div>
                <span className={`preview-status ${formData.enable ? 'active' : 'inactive'}`}></span>
              </div>
              
              <div className="preview-content">
                <div className="preview-info">
                  <h4>{formData.name || 'Country Name'}</h4>
                  <p className="preview-code">{formData.code || 'CODE'}</p>
                </div>

                <div className="preview-badges">
                  <span className={`preview-badge ${formData.enable ? 'active' : 'inactive'}`}>
                    {formData.enable ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-box">
              <h4>🌍 Country Guidelines</h4>
              <ul>
                <li>Use official country names</li>
                <li>Country codes should follow ISO 3166-1 alpha-2 standard</li>
                <li>Keep codes uppercase for consistency</li>
                <li>Verify the code doesn't conflict with existing countries</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={() => navigate('/dashboard/countries')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCountry;
